import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { useAddChecklistTodo } from './useAddChecklistTodo';

interface AddTodoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyEnrollmentId: string;
  /** The children of the year a to-do can be for; otherwise it is for the family. */
  childOptions: { id: string; name: string }[];
  onSuccess?: () => void;
}

const NEW_FORM = { title: '', details: '', personId: '', required: true, officeOnly: false };

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

// The office adds a to-do to this family's checklist. The server's answer
// decides: any refusal stays on screen and the dialog stays open.
export function AddTodoDialog({ open, onOpenChange, familyEnrollmentId, childOptions, onSuccess }: AddTodoDialogProps) {
  const [form, setForm] = useState(NEW_FORM);
  const [errors, setErrors] = useState<string[]>([]);
  const [addTodo, { loading }] = useAddChecklistTodo();

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setForm(NEW_FORM);
      setErrors([]);
    }
    onOpenChange(next);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setErrors(['Add a title']);
      return;
    }
    try {
      const result = await addTodo({
        variables: {
          input: {
            familyEnrollmentId,
            personId: form.personId || null,
            title: form.title.trim(),
            details: form.details.trim() || null,
            required: form.required,
            officeOnly: form.officeOnly,
          },
        },
      });
      const payload = result.data?.addChecklistTodo;
      if (payload?.errors && payload.errors.length > 0) {
        setErrors(payload.errors);
        return;
      }
      if (!payload?.item) {
        setErrors(['The to-do was not added. Please try again.']);
        return;
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The to-do was not added. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a to-do</DialogTitle>
          <DialogDescription>A step for this family's checklist that isn't part of the school's usual steps.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="todo-title">Title</Label>
            <Input id="todo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="todo-details">Details (optional)</Label>
            <Textarea id="todo-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="todo-for">For</Label>
            <select id="todo-for" className={selectClass} value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
              <option value="">The family</option>
              {childOptions.map((child) => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="todo-required" checked={form.required} onCheckedChange={(checked) => setForm({ ...form, required: checked === true })} />
            <Label htmlFor="todo-required">Required</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="todo-office-only" checked={form.officeOnly} onCheckedChange={(checked) => setForm({ ...form, officeOnly: checked === true })} />
            <Label htmlFor="todo-office-only">Only the office sees this</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add to-do'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
